import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireAuth } from './RequireAuth';
import { AppLayout } from './AppLayout';
import { SplashLoader } from './SplashLoader';
import { trackPageView } from '@/shared/lib/metrika';

const Login = lazy(() => import('@/pages/login/LoginPage'));
const ResetPassword = lazy(() => import('@/pages/reset-password/ResetPasswordPage'));
const AcceptInvite = lazy(() => import('@/pages/accept-invite/AcceptInvitePage'));
const Listings = lazy(() => import('@/pages/listings/ListingsPage'));
const CollectionsLayout = lazy(() => import('@/pages/collections/CollectionsLayout'));
const Collections = lazy(() => import('@/pages/collections/CollectionsPage'));
const CollectionDetail = lazy(() => import('@/pages/collections/CollectionDetailPage'));
const Profile = lazy(() => import('@/pages/profile/ProfilePage'));
const OrgSettings = lazy(() => import('@/pages/OrgSettingsPage'));
const NotFound = lazy(() => import('@/pages/_errors/NotFound'));
const Forbidden = lazy(() => import('@/pages/_errors/Forbidden'));

function wrap(Component: ComponentType) {
  return (
    <Suspense fallback={<SplashLoader />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: wrap(Login) },
  { path: '/reset-password', element: wrap(ResetPassword) },
  { path: '/accept-invite', element: wrap(AcceptInvite) },
  { path: '/forbidden', element: wrap(Forbidden) },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/listings" replace /> },
      { path: '/listings', element: wrap(Listings) },
      {
        path: '/collections',
        element: wrap(CollectionsLayout),
        children: [
          { index: true, element: wrap(Collections) },
          { path: ':id', element: wrap(CollectionDetail) },
        ],
      },
      { path: '/profile', element: wrap(Profile) },
      { path: '/org/settings', element: wrap(OrgSettings) },
    ],
  },
  { path: '*', element: wrap(NotFound) },
]);

let lastTrackedUrl = window.location.href;
router.subscribe((state) => {
  const url = window.location.origin + state.location.pathname + state.location.search;
  if (url === lastTrackedUrl) return;
  lastTrackedUrl = url;
  trackPageView(url);
});
